/**
 * Shared logic for the `sentiment` question type used by live assessments.
 *
 * A sentiment scale is an ordered set of choices where the lowest value reads as
 * most negative and the highest as most positive. A "decoration" style turns a
 * choice's rank within the scale into an icon, and every color comes from the
 * shared `scaleColorTraffic` so the editor, the participant view, the host view
 * and the review charts can never disagree.
 */

import {
  faThumbsUp,
  faThumbsDown,
  faHandPaper,
  faFaceFrown,
  faFaceGrimace,
  faFaceGrin,
  faFaceGrinHearts,
  faFaceMeh,
  faFaceNoseSteam,
  faFaceSadCry,
  faFaceSmile,
  faFaceSmirking,
  faFaceUnamused,
  faCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircle as faCircleSolid } from '@fortawesome/pro-solid-svg-icons';
import {
  darkInkColorFor,
  fadeColor,
  inkColorFor,
  scaleColorTraffic,
  toHexColor,
} from 'js/utils/color';
import { getAnswerDomain } from 'js/utils/scoring';

/**
 * The scale interpolates to `rgb(...)`; hex is what the rest of the codebase
 * uses and what the contrast helper needs.
 */
export function sentimentColor(fraction) {
  return toHexColor(scaleColorTraffic(clamp01(Number(fraction))));
}

/**
 * Glyph ink on a `sentimentColor` fill. Looks up the dark and light traffic
 * ink scales, then keeps the one farther away in luminance so yellow stays
 * `#756900` and red/green go light.
 */
export function sentimentInkColor(fraction) {
  return inkColorFor(fraction, sentimentColor(fraction));
}

/** Dark traffic ink for a `fadeColor` wash, where a light glyph would vanish. */
export function sentimentWashInkColor(fraction) {
  return darkInkColorFor(fraction);
}

export const SENTIMENT_TYPE = 'sentiment';

/** Authoring guideline, not a rendering limit. Exceeding it only warns. */
export const MIN_SENTIMENT_OPTIONS = 2;
export const MAX_SENTIMENT_OPTIONS = 10;

export const DEFAULT_DECORATION = 'thumbs';

/** FontAwesome shrink on a stacked circle. Faces sit larger than thumbs. */
export const DECORATION_SHRINK = Object.freeze({
  thumbs: 8,
  faces: 6,
  colors: 8,
});

export function decorationShrink(style = DEFAULT_DECORATION) {
  return DECORATION_SHRINK[style] ?? DECORATION_SHRINK[DEFAULT_DECORATION];
}

/**
 * Deflects the thumb toward horizontal as it approaches the middle of the
 * scale. A two-point scale is therefore plain down/up. Exactly 0.5 is a
 * raised hand rather than a sideways thumb.
 */
function thumbsDecoration({ fraction }) {
  if (fraction === 0.5) {
    return { icon: faHandPaper, rotate: 0 };
  }
  if (fraction > 0.5) {
    return {
      icon: faThumbsUp,
      rotate: normalizeRotation(-(1 - fraction) * 180),
    };
  }
  return { icon: faThumbsDown, rotate: normalizeRotation(fraction * 180) };
}

/**
 * The 10-point face palette, most positive first. Smaller scales take a
 * symmetric subset around `meh` so 2 is smile/frown and 3 is smile/meh/frown,
 * then smirking/unamused and the extremes fill in as the count grows.
 */
const FACE_SCALE = Object.freeze([
  faFaceGrinHearts,
  faFaceGrin,
  faFaceSmile,
  faFaceSmirking,
  faFaceMeh,
  faFaceUnamused,
  faFaceFrown,
  faFaceGrimace,
  faFaceSadCry,
  faFaceNoseSteam,
]);

/**
 * Most-positive-first subsets of `FACE_SCALE`. `decorationFor` reverses these
 * so index 0 is the lowest (most negative) option, matching thumbs.
 */
const FACE_SETS_POSITIVE_FIRST = Object.freeze({
  2: [faFaceSmile, faFaceFrown],
  3: [faFaceSmile, faFaceMeh, faFaceFrown],
  4: [faFaceSmile, faFaceSmirking, faFaceUnamused, faFaceFrown],
  5: [faFaceSmile, faFaceSmirking, faFaceMeh, faFaceUnamused, faFaceFrown],
  6: [
    faFaceGrin,
    faFaceSmile,
    faFaceSmirking,
    faFaceUnamused,
    faFaceFrown,
    faFaceGrimace,
  ],
  7: [
    faFaceGrin,
    faFaceSmile,
    faFaceSmirking,
    faFaceMeh,
    faFaceUnamused,
    faFaceFrown,
    faFaceGrimace,
  ],
  8: [
    faFaceGrinHearts,
    faFaceGrin,
    faFaceSmile,
    faFaceSmirking,
    faFaceUnamused,
    faFaceFrown,
    faFaceGrimace,
    faFaceSadCry,
  ],
  9: [
    faFaceGrinHearts,
    faFaceGrin,
    faFaceSmile,
    faFaceSmirking,
    faFaceMeh,
    faFaceUnamused,
    faFaceFrown,
    faFaceGrimace,
    faFaceSadCry,
  ],
  10: FACE_SCALE,
});

function facesLowestFirst(count) {
  const total = Number(count);
  const positiveFirst = FACE_SETS_POSITIVE_FIRST[total];
  if (positiveFirst) {
    return [...positiveFirst].reverse();
  }

  if (total === 1) {
    return [FACE_SCALE[0]];
  }

  if (total > 10) {
    const last = FACE_SCALE.length - 1;
    return Array.from({ length: total }, (_, index) => {
      const palIndex = Math.round((index / (total - 1)) * last);
      return FACE_SCALE[last - palIndex];
    });
  }

  return [...FACE_SETS_POSITIVE_FIRST[3]].reverse();
}

function facesDecoration({ fraction, index, count }) {
  const set = facesLowestFirst(count);
  if (set && Number.isFinite(Number(index))) {
    const i = Math.max(0, Math.min(set.length - 1, Math.round(Number(index))));
    return { icon: set[i], rotate: 0 };
  }

  const palette = facesLowestFirst(10);
  const i = Math.round(clamp01(fraction) * (palette.length - 1));
  return { icon: palette[i], rotate: 0 };
}

/** Collapses `-0` to `0` so callers and tests see a single zero. */
function normalizeRotation(rotate) {
  return rotate === 0 ? 0 : rotate;
}

function colorsDecoration() {
  return { icon: faCircleSolid, rotate: 0 };
}

const STYLES = {
  thumbs: thumbsDecoration,
  faces: facesDecoration,
  colors: colorsDecoration,
};

export const DECORATION_STYLES = Object.freeze([
  { key: 'thumbs', name: 'Thumbs', icon: faThumbsUp },
  { key: 'faces', name: 'Smileys', icon: faFaceSmile },
  { key: 'colors', name: 'Colors', icon: faCircle },
]);

export function isSupportedDecoration(style) {
  return Object.prototype.hasOwnProperty.call(STYLES, style);
}

/**
 * Builds a FontAwesome `transform` string. Negative rotations use FontAwesome's
 * double-dash form (`rotate--45`).
 */
export function decorationTransform(rotate, shrink = 7) {
  const parts = [];
  if (shrink) {
    parts.push(`shrink-${shrink}`);
  }
  const rounded = Math.round(rotate);
  if (rounded !== 0) {
    parts.push(`rotate-${rounded < 0 ? `-${Math.abs(rounded)}` : rounded}`);
  }
  return parts.join(' ');
}

function decorationFrom({
  fraction,
  index,
  count,
  style = DEFAULT_DECORATION,
}) {
  const f = clamp01(Number(fraction));
  const resolvedStyle = STYLES[style] ? style : DEFAULT_DECORATION;
  const build = STYLES[resolvedStyle];
  const { icon, rotate } = build({ fraction: f, index, count });
  const shrink = decorationShrink(resolvedStyle);

  return {
    icon,
    rotate,
    shrink,
    colorOnly: resolvedStyle === 'colors',
    fraction: f,
    color: sentimentColor(f),
    ink: sentimentInkColor(f),
    wash: fadeColor(sentimentColor(f)),
    washInk: sentimentWashInkColor(f),
    transform: decorationTransform(rotate, shrink),
    bucket: trafficBucketFor(f),
  };
}

/**
 * Decoration for the choice at `index` (0 = lowest value) of a `count`-option
 * scale. The math is intentionally unbounded: any count yields a valid ramp.
 */
export function decorationFor(index, count, style = DEFAULT_DECORATION) {
  const total = Number(count);
  const i = Number(index);
  const fraction = total > 1 ? clamp01(i / (total - 1)) : 1;

  return decorationFrom({ fraction, index: i, count: total, style });
}

/** The decoration at an arbitrary point on the scale. */
export function decorationAt(fraction, style = DEFAULT_DECORATION) {
  return decorationFrom({ fraction: clamp01(Number(fraction)), style });
}

/**
 * Decoration for an aggregate, such as the mean of a question's responses.
 *
 * The fraction is snapped to the nearest option rank so a summary icon is always
 * one of the icons participants actually saw, rather than an in-between
 * rotation no option carries.
 */
export function decorationForFraction(
  fraction,
  count,
  style = DEFAULT_DECORATION
) {
  const total = Number(count);
  if (!Number.isFinite(total) || total < 2) {
    return decorationAt(fraction, style);
  }

  const rank = Math.round(clamp01(Number(fraction)) * (total - 1));
  return decorationFor(rank, total, style);
}

function clamp01(value) {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(1, Math.max(0, value));
}

/**
 * Maps a raw answer value onto 0..1 using the question's answer domain rather
 * than its option count, because a borrowed answer set may be valued `1..5`
 * instead of `0..4`. Pass the result to `scaleColorTraffic` for a color.
 */
export function normalizedValue(value, domain) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || !domain || domain.length === 0) {
    return null;
  }

  const min = Number(domain[0]);
  const max = Number(domain[domain.length - 1]);

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }
  if (min === max) {
    return 1;
  }

  return clamp01((numeric - min) / (max - min));
}

/** Convenience wrapper: the answer domain for a question's answers/choices. */
export function sentimentDomain(answers) {
  return getAnswerDomain(answers ?? [], 'value', 2, [0, 0]);
}

/**
 * Coarse three-way bucket for surfaces painted a single color (the question
 * boxes on the live assessment detail). Thresholds are the normalized form of
 * the original `>= 1.5` / `>= 0.5` rule on a `[0, 2]` domain.
 */
export function trafficBucketFor(fraction) {
  if (fraction == null || !Number.isFinite(Number(fraction))) {
    return null;
  }
  const f = Number(fraction);
  if (f >= 0.75) {
    return 'green';
  }
  if (f >= 0.25) {
    return 'yellow';
  }
  return 'red';
}

/** `{ 'vote-green': true }`-style class map, or `{}` when there is no value. */
export function voteClassesFor(fraction) {
  const bucket = trafficBucketFor(fraction);
  return bucket ? { [`vote-${bucket}`]: true } : {};
}

/**
 * Missing or unknown types resolve to sentiment so older live rows keep
 * working. Types the live editor offers, including scored and informational
 * Likert, pass through.
 */
export function resolveLiveQuestionType(question) {
  const type = question?.type;
  if (
    type === 'single-select' ||
    type === 'long-text' ||
    type === 'multi-select' ||
    type === 'likert' ||
    type === 'unscored-likert' ||
    type === SENTIMENT_TYPE
  ) {
    return type;
  }
  return SENTIMENT_TYPE;
}

export function isLiveSentimentQuestion(question) {
  return resolveLiveQuestionType(question) === SENTIMENT_TYPE;
}

/** The decoration style for a question, defaulting to thumbs. */
export function decorationForQuestion(question) {
  const style = question?.config?.decoration ?? question?.decoration;
  return isSupportedDecoration(style) ? style : DEFAULT_DECORATION;
}

/**
 * Glance tiles for Likert use Colors: a scored wash and a solid dot, not
 * thumbs or faces the participant never saw on the ballot.
 */
export function decorationStyleForLiveTile(question) {
  const type = resolveLiveQuestionType(question);
  if (type === 'likert' || type === 'unscored-likert') {
    return 'colors';
  }
  return decorationForQuestion(question);
}

/**
 * Ranks choices by numeric value ascending so the lowest value always receives
 * the most negative decoration. The editor lists choices lowest-first while the
 * runtime renders highest-first, so the decoration index must come from this
 * rank rather than from whatever order is being displayed.
 */
export function rankByValue(answers) {
  const scored = (answers ?? []).filter((a) => !a?.is_na);
  const ordered = [...scored].sort((a, b) => Number(a.value) - Number(b.value));
  const rankByGuid = new Map();
  ordered.forEach((answer, index) => {
    rankByGuid.set(answer.guid ?? answer.id, index);
  });

  return { ordered, count: ordered.length, rankByGuid };
}

/**
 * Decorated options for a question, ordered lowest value first. `is_na` choices
 * are excluded from the scale and from the option count.
 */
export function decoratedOptions(question) {
  const style = decorationForQuestion(question);
  const { ordered, count } = rankByValue(question?.answers ?? []);

  return ordered.map((answer, index) => ({
    ...answer,
    ...decorationFor(index, count, style),
  }));
}
